import { useState } from 'react';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

/**
 * Shared submission logic for Readdy built-in forms.
 * Reads the real DOM form via FormData so browser AutoFill values are captured,
 * silently ignores honeypot hits, and derives UI state from the server response.
 */
export function useFormSubmit(endpoint: string, successMessage: string) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [message, setMessage] = useState('');

  const submit = async (form: HTMLFormElement) => {
    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') ?? '').trim();

    if (honeypot) {
      setStatus('success');
      setMessage(successMessage);
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setMessage(successMessage);
        form.reset();
      } else {
        setStatus('error');
        setMessage(
          typeof serverMessage === 'string' && serverMessage.trim()
            ? serverMessage
            : '提交失败，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  return { status, message, submit };
}